import Section from '../../_Misc/Section';
import { Button, Checkbox, Descriptions, Empty, Flex, Tabs, TabsProps } from 'antd';
import { Buttons, LeadingContent } from '../style';
import { Table } from '../../../../../componentsV2/Table';
import {
  DeleteOutlined,
  FilterOutlined,
  MinusCircleOutlined,
  MoreOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import { memo, useCallback, useMemo, useState } from 'react';
import { formatDate } from '../../../../../utils/dateUtils';
import Dropdown from 'antd/es/dropdown/dropdown';
import { format } from 'date-fns';
import { removeFilterView } from '../../../../../features/saved-filters/remove';
import { useToast } from '../../../../../hooks/useToast';
import { setCurrentView } from '../../../../../features/saved-filters/set-current-view';
import { useAccountSearch } from '../../../../../features/account/account-search/account-search.hook';
import { useOrganizationMembers } from '../../../../../features/organizations/hooks/useOrganizationMembers';
import {
  FilterValue,
  SavedFilterItemView,
  SavedFiltersFilterItem,
  SavedFilters as SavedFiltersType,
} from '../../../../../features/saved-filters/types';

export const SavedFilters: React.FC = () => {
  const { success } = useToast();

  const { accounts } = useAccountSearch();
  const { members } = useOrganizationMembers();

  const [expandRow, setExpandRow] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<string>('0');

  const savedFilters = localStorage.getItem('savedFilters') || '{}';
  const parsedFilters: SavedFiltersType = JSON.parse(savedFilters);

  const [renderKey, setRenderKey] = useState(Date.now());

  const NoValueProvidedComponent = memo(() => (
    <span style={{ color: 'lightgrey' }}>
      <em>No value provided.</em>
    </span>
  ));

  const renderValue = useCallback((value: FilterValue, type: string) => {
    if (type === 'dateRange') {
      if ((value as string[])[0] === null && (value as string[])[1] === null) {
        return <NoValueProvidedComponent />;
      }

      return (value as string[]).map((date: string, index: number) => (
        <span key={index}>
          {format(new Date(date), 'MM/dd/yyyy')}
          {index < (value as string[]).length - 1 && ' - '}
        </span>
      ));
    }

    if (type === 'dateRangeType') {
      if (value === 'custom') {
        return <span>Custom</span>;
      }

      const [days] = (value as string).split('d');
      return <span>{days} days</span>;
    }

    if (type === 'text' && !value) {
      return <NoValueProvidedComponent />;
    }

    if (type === 'boolean') {
      return (
        <Checkbox checked={value as boolean} disabled>
          {value ? 'Yes' : 'No'}
        </Checkbox>
      );
    }

    if (type === 'multipleAccounts') {
      if ((value as string[]).length === 0) {
        return <NoValueProvidedComponent />;
      }

      return (value as string[]).map((account: string, index: number) => (
        <span key={index}>
          {accounts.find(a => a.id === account)?.name}
          {index < (value as string[]).length - 1 && ' - '}
        </span>
      ));
    }

    if (type === 'accountsJson') {
      const parsedAccounts = JSON.parse(value as string);

      if (parsedAccounts.length === 0) {
        return <NoValueProvidedComponent />;
      }

      return parsedAccounts.map((account: { id: string; name: string }, index: number) => (
        <span key={index}>
          {account.name}
          {index < parsedAccounts.length - 1 && ' - '}
        </span>
      ));
    }

    if (type === 'owners') {
      if ((value as string[]).length === 0) {
        return <NoValueProvidedComponent />;
      }

      return (value as string[]).map((owner: string, index: number) => {
        const user = members.find(m => m.user.id === owner);
        return (
          <span key={index}>
            {user?.user.firstName} {user?.user.lastName}
            {index < (value as string[]).length - 1 && ' - '}
          </span>
        );
      });
    }

    return <span>{value}</span>;
  }, []);

  const items: TabsProps['items'] = useMemo(
    () =>
      Object.entries(parsedFilters).map(([key, value], index: number) => ({
        key: index.toString(),
        label: value.pageTitle,
        children: (
          <Table
            pagination={false}
            loading={false}
            rowKey={row => row.id}
            columns={[
              {
                title: 'View Name',
                key: 'name',
                dataIndex: 'name',
              },
              {
                title: 'Created At',
                key: 'createdAt',
                dataIndex: 'createdAt',
                render: (value: string) => <span>{format(new Date(value), 'MM/dd/yyyy')}</span>,
              },
              {
                title: 'Actions',
                key: 'actions',
                render: (_, record) => (
                  <Dropdown
                    trigger={['click']}
                    menu={{
                      items: [
                        {
                          label: 'Apply',
                          icon: <FilterOutlined />,
                          key: 'apply',
                          onClick: () => {
                            setCurrentView(key, record.id);
                            success('Filter view applied successfully');
                            setRenderKey(Date.now());
                          },
                        },
                        {
                          label: 'Delete',
                          icon: <DeleteOutlined />,
                          key: 'delete',
                          danger: true,
                          onClick: () => {
                            removeFilterView(key, record.id);
                            setRenderKey(Date.now());
                          },
                        },
                      ],
                    }}
                  >
                    <Button type="default" icon={<MoreOutlined />} size="small" />
                  </Dropdown>
                ),
              },
            ]}
            data={Object.values(value.filterViews) || []}
            props={{
              expandable: {
                expandedRowKeys: expandRow,
                expandIcon: (row: { expanded: boolean; record: SavedFilterItemView }) => (
                  <Button
                    icon={row.expanded ? <MinusCircleOutlined /> : <PlusCircleOutlined />}
                    onClick={() => {
                      if (expandRow.includes(row.record.id)) {
                        setExpandRow(expandRow.filter(id => id !== row.record.id));
                      } else {
                        setExpandRow([...expandRow, row.record.id]);
                      }
                    }}
                  />
                ),
                expandedRowRender: (record: SavedFilterItemView) => {
                  return (
                    <Descriptions
                      bordered
                      layout="vertical"
                      colon
                      items={Object.values(record.filters).map(
                        (filter: SavedFiltersFilterItem, index: number) => ({
                          key: index,
                          label: filter.label,
                          children: renderValue(filter.value, filter.type),
                        }),
                      )}
                    />
                  );
                },
              },
            }}
          />
        ),
      })),
    [renderKey, expandRow],
  );

  return (
    <Section title="Saved filters" description="Manage your saved filters">
      <LeadingContent style={{ width: '100%' }}>
        {items.length > 0 ? (
          <Tabs items={items} activeKey={activeTab} onChange={setActiveTab} />
        ) : (
          <Flex align="center" justify="center">
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No saved filters" />
          </Flex>
        )}
      </LeadingContent>
    </Section>
  );
};
